import React from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { countryCodesMap, formatCurrency, Tenant } from '@p81-common/ui';
import type { DataGridProps, GridColDef } from '@p81-common/ui';
import { env } from 'env';
import { useRegionalLink } from 'hooks/useRegionalLink';

export const useTableSettings = () => {
  const { url } = useRouteMatch();
  const generateRegionalPath = useRegionalLink();

  const tableConfig: GridColDef[] = [
    {
      isSortable: false,
      field: 'companyName',
      title: 'Company Name',
      show: true,
      width: 228,
      fieldsToExport: [],
      renderCell: params => {
        const { row } = params;
        const country = row?.country?.toString();
        //@ts-ignore
        const countryData = countryCodesMap[country];
        const countryFlag = countryData?.flag || 'worldwide';
        const countryFlagSrc = `${env.REACT_APP_STATIC_ASSETS_URL}/flags/${countryFlag}.svg`;
        return (
          <NavLink
            to={generateRegionalPath(
              `${url}/${row.customerId}/platform/networks`
            )}
          >
            <Tenant
              companyIconProps={{
                badgeProps: {
                  badgeContent: (
                    <img
                      alt="flag"
                      height="16px"
                      src={countryFlagSrc}
                      width="16px"
                    />
                  )
                },
                domain: row?.website
              }}
              subTexts={[row?.companySize, row?.customerType]}
              title={row?.companyName}
            />
          </NavLink>
        );
      }
    },
    {
      isSortable: false,
      field: '_id',
      title: 'Network ID',
      width: 174,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'networkName',
      title: 'Network Name',
      width: 174,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'networkGwMinVersion',
      title: 'Network Version',
      width: 174,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'gwCount',
      title: 'GW Count',
      width: 188,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'networkDns',
      title: 'Network DNS',
      width: 174,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'pricingPlan',
      title: 'Customer Pricing Plan',
      width: 216,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'arr',
      title: 'Tenant ARR',
      width: 174,
      fieldsToExport: [],
      renderCell(params) {
        const { row } = params;
        return row.arr ? formatCurrency(row.arr) : 'N/A';
      }
    },
    {
      isSortable: false,
      field: 'billingPlan',
      title: 'Billing Plan',
      width: 174,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'accManager',
      title: 'Account Manager',
      type: 'userName',
      width: 174,
      fieldsToExport: []
    },
    {
      isSortable: false,
      field: 'csEngineer',
      title: 'CS Engineer',
      type: 'userName',
      width: 174,
      fieldsToExport: []
    }
  ];

  const detailPanelTableConfig: DataGridProps['tableConfig'] = [
    {
      sortable: true,
      field: 'gatewayId',
      headerName: 'Gateway ID',
      width: 210
    },
    {
      cellEmptyState: 'N/A',
      sortable: true,
      field: 'gatewayIp',
      headerName: 'Gateway IP',
      width: 174
    },
    {
      isSortable: false,
      field: 'regionName', // the field supported by GridTextGroupCell
      type: 'textGroup',
      title: 'Region',
      width: 174,
      fieldsToExport: [],
      getInnerProps: () => ({
        // override avatar
        avatarProps: {
          size: 'small'
        }
      })
    },
    {
      cellEmptyState: 'N/A',
      sortable: true,
      field: 'gatewayImageVersion',
      headerName: 'Gateway Version',
      width: 200
    }
  ];

  return {
    detailPanelTableConfig,
    tableConfig
  };
};
